import { getSearchIndex, getTable } from '../utils/search/helpers'
import { exactMatch, all, any } from '../utils/search/filterSyntax'
import { initLazyLoadedImages } from './listing_tile_rendering/listingImageRenderer'
import { getWishlistedItemsFromPage, getHitHTML } from './listing_tile_rendering/listingTileUtils'
import setUpAddToWishlistBehaviour from './addToWishlist'
import { setOpenModalButtons } from './modals'
import setMaskedUrls from './maskedUrls'
import { bindCarouselImpression } from './analytics'
import { toProperCase, getModifierFromLocation } from '../utils/search/seoRouting'
import { stripLocaleFromPath } from '../utils/localeHelpers'

function facetFilter (key, value) {
  return exactMatch(key, value)
}

function buildCategoryHierarchyFilters (segments) {
  const properCased = segments.map(s => toProperCase(s))
  return properCased.map((_, i) => {
    const chain = properCased.slice(0, i + 1).join(' > ')
    return facetFilter(`category_hierarchy.lvl${i}`, chain)
  })
}

// Collect disjunctive facet refinements (dFR) from PLP query params.
// Groups multiple values per facet for OR combination.
function collectDisjunctiveFacets (searchParams) {
  const pattern = /^dFR\[(.+?)]\[\d+]$/
  const facets = {}
  searchParams.forEach((value, key) => {
    const match = key.match(pattern)
    if (!match) return
    const facet = match[1]
    if (!facets[facet]) facets[facet] = []
    facets[facet].push(value)
  })
  return facets
}

function buildDisjunctiveFilters (facets) {
  return Object.entries(facets).map(([facet, values]) => {
    return any(values.map(v => facetFilter(facet, v)))
  })
}

function collectHierarchicalFilters (searchParams) {
  const pattern = /^hFR\[(.+?)]\[\d+]$/
  return Array.from(searchParams.entries())
    .filter(([key]) => pattern.test(key))
    .map(([key, value]) => facetFilter(key.match(pattern)[1], value))
}

function collectTagFilters (searchParams) {
  const pattern = /^tR\[\d+]$/
  return Array.from(searchParams.entries())
    .filter(([key]) => pattern.test(key))
    .map(([, value]) => facetFilter('_tags', value))
}

// Parse PLP-style dFR/hFR/tR query params into Algolia filter strings.
// Parallels searchRouting.js:parseURL but outputs raw filter strings for the Algolia API.
function parsePlpFilterParams (searchParams) {
  return [
    ...buildDisjunctiveFilters(collectDisjunctiveFacets(searchParams)),
    ...collectHierarchicalFilters(searchParams),
    ...collectTagFilters(searchParams)
  ]
}

function buildSearchFilters (url) {
  const tag = url.searchParams.get('tag') || ''
  const filters = []
  if (tag) filters.push(facetFilter('_tags', tag))
  filters.push(...parsePlpFilterParams(url.searchParams))
  return filters
}

function buildCategoryFilters (segments, routesHashtable, modifiers) {
  const [modifierIndex, modifierKey, modifierValue] = getModifierFromLocation(segments, modifiers)
  const categorySegments = modifierIndex > 0 ? segments.slice(0, modifierIndex) : segments
  const filters = buildCategoryHierarchyFilters(categorySegments)
  if (modifierKey && modifierValue) {
    filters.push(facetFilter(modifierKey, modifierValue))
  }
  return filters
}

function withBaseFilter (extraFilters) {
  return all(['sold:false', ...extraFilters])
}

function parseSearchUrl (searchUrl, routesHashtable, modifiers) {
  try {
    const url = new URL(searchUrl, window.location.origin)
    const pathname = stripLocaleFromPath(url.pathname)
    const segments = pathname.split('/').filter(Boolean)
    const query = url.searchParams.get('q') || ''

    if (segments[0] === 'search') return { query, filters: withBaseFilter(buildSearchFilters(url)) }
    if (segments[0] === 'new') return { query: '', filters: withBaseFilter(['published_recently:true']) }
    if (segments[0] === 'sale') return { query: '', filters: withBaseFilter(['sale:true']) }
    if (segments[0] === 'sellers' && segments[1]) return { query: '', filters: withBaseFilter([facetFilter('user_slug', segments[1])]) }

    if (routesHashtable && segments.length > 0 && routesHashtable[segments[0]]) {
      return { query: '', filters: withBaseFilter(buildCategoryFilters(segments, routesHashtable, modifiers)) }
    }

    return { query, filters: 'sold:false', unresolved: !query }
  } catch (e) {
    return { query: '', filters: 'sold:false', unresolved: true }
  }
}

function loadArticleCarousel (container, routesHashtable, modifiers) {
  const searchUrl = container.getAttribute('data-search-url')
  if (!searchUrl) return Promise.resolve()

  // Showcase URLs are resolved server-side
  const showcaseFilters = container.getAttribute('data-showcase-filters')
  if (showcaseFilters) {
    return searchAndRender(container, '', all(['sold:false', showcaseFilters]))
  }

  const params = parseSearchUrl(searchUrl, routesHashtable, modifiers)
  // Articles keep the catalogue fallback; a merchandised heading over random stock would mislead.
  if (params.unresolved && container.dataset.carouselStyle === 'standard-2025') {
    container.closest('.vjs-page-builder-block')?.classList.add('hidden')
    return Promise.resolve()
  }

  return searchAndRender(container, params.query, params.filters)
}

function searchAndRender (container, query, filters) {
  const index = getSearchIndex()

  return index.search(query, {
    hitsPerPage: 20,
    page: 0,
    filters,
    attributesToRetrieve: ['*', '-description']
  }).then(results => {
    const queryID = results.queryID ? results.queryID : ''
    results.hits.forEach((hit, index) => {
      hit.__position = index + 1
      hit.__queryID = queryID
    })

    const wishlistedItems = getWishlistedItemsFromPage()
    const isLoggedIn = JSON.parse(document.querySelector('body').getAttribute('data-logged-in'))
    const isTradeBuyer = JSON.parse(container.dataset.tradeBuyer || 'false')
    const userStatus = { isTradeBuyer, isLoggedIn }
    // The 2025 tile and three-across slider the homepage uses; plpPage is what selects that tile.
    const standard2025 = container.dataset.carouselStyle === 'standard-2025'
    const tileOptions = { showViewSimilar: false, showCategoryLink: false, plpPage: standard2025 }

    const hitsContainer = container.querySelector('.vjs-article-carousel-hits')
    if (!hitsContainer) return

    hitsContainer.innerHTML = results.hits.map(hit => getHitHTML(hit, wishlistedItems, userStatus, tileOptions)).join('')
    bindCarouselImpression(hitsContainer)

    return import(/* webpackChunkName: "carousels" */ './carousels').then(module => (
      standard2025 ? module.initVisualMerchandisingCarousel(hitsContainer) : module.articleProductCarousel(hitsContainer)
    ))
  })
}

export default function initArticleProductCarousels () {
  const carousels = document.querySelectorAll('.vjs-product-carousel')
  if (!carousels.length) return

  // Parse route data once, shared across all carousels
  const routesHashtable = getTable('path-hierarchy') || {}
  const modifiers = getTable('modifiers') || {}

  const promises = Array.from(carousels).map(c => loadArticleCarousel(c, routesHashtable, modifiers))

  Promise.allSettled(promises).then(() => {
    initLazyLoadedImages()
    setOpenModalButtons()
    setUpAddToWishlistBehaviour()
    setMaskedUrls()
  })
}
